import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Stack, styled, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { DividerWithLabel, ICONS, LinkUnstyled, unit } from '@hcr/ui'
import { isNotNull, isNotUndefined, isNull } from '@hcr/utils'

import { COLORS, Z_INDEX } from '../../../../../../libs/ui/src/lib/constants'
import { SENTRY_CONFIG } from '../../../configs'
import { ReferenceName, useLastPath, useSkipNavigation, useUserDetails } from '../../../contexts'
import { useRelogin } from '../../../hooks'
import { AccountSearchParamsKeys } from '../../../models'
import { LocaleNamespace } from '../../../models/i18n'
import { Path } from '../../../models/navigation'
import { to } from '../../../utils/navigation'
import { ButtonSignOut, LayoutNavigationBackError500, Metadata } from '../../common'
import { LayoutNavigation } from '../../common/layout-navigation'

import { AccountLoading } from './AccountLoading'
import { UpdateEmailVerificationExpired } from './common'
import { Link } from './common/link'
import { Ownership } from './common/ownership'
import { Settings } from './common/settings'
import { UpdateEmailStatusMessage } from './common/update-email/UpdateEmailStatusMessage'

export const Account: FC = () => {
  const { t } = useTranslation()
  const lastPath = useLastPath()
  const [searchParams] = useSearchParams()
  const nonceSearchParam = searchParams.get(AccountSearchParamsKeys.Nonce)
  const statusSearchParam = searchParams.get(AccountSearchParamsKeys.Status)

  const userDetails = useUserDetails()
  const { registerRef } = useSkipNavigation()

  useRelogin()

  if (isNull(nonceSearchParam) && statusSearchParam === 'expired') {
    return <UpdateEmailVerificationExpired />
  }

  if (userDetails.isError) {
    return <LayoutNavigationBackError500 to={lastPath.root} />
  }

  if (userDetails.isSuccess && isNotUndefined(userDetails.data)) {
    return (
      <LayoutNavigation showSideNavigation={false}>
        <Metadata title={t('page-title.account')} />
        <ArrowLinkIcon
          ref={registerRef(ReferenceName.ACCOUNT_BACK_BUTTON)}
          to={lastPath.root}
          aria-label={t('navigation.back')}
        >
          <Stack paddingX={unit(3)} paddingY={unit(2)} direction={'row'} alignItems={'center'}>
            <FontAwesomeIcon style={{ padding: unit(3) }} icon={ICONS.farArrowLeft} />
            <Typography component='span' variant='titleM'>
              {t('navigation.back')}
            </Typography>
          </Stack>
        </ArrowLinkIcon>
        <Header>
          <Typography variant='headlineS' sx={visuallyHidden}>
            {userDetails.data.first_name} {userDetails.data.last_name}
          </Typography>{' '}
          <Typography
            component='h1'
            className={SENTRY_CONFIG.privacy.className}
            variant='headlineS'
            lineHeight={unit(8)}
          >
            {userDetails.data.first_name} {userDetails.data.last_name}
          </Typography>
        </Header>
        <Stack component='ul' tabIndex={-1}>
          <DividerWithLabel component='h2'>{t('account.personal-information')}</DividerWithLabel>
          <Stack component='li'>
            <UserInformation>
              <Typography component='dt' className={SENTRY_CONFIG.privacy.className} variant='labelM'>
                {t('account.address')}
              </Typography>
              <Stack component='dd'>
                <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
                  {userDetails.data.street_address}
                </Typography>
                {isNotNull(userDetails.data.street_address2) && (
                  <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
                    {userDetails.data.street_address2}
                  </Typography>
                )}
                <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
                  {userDetails.data.postal_code} {userDetails.data.city}
                </Typography>
                <Typography className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
                  {t(userDetails.data.country, { ns: LocaleNamespace.Country })}
                </Typography>
              </Stack>
            </UserInformation>
            <ListButton component={LinkUnstyled} to={to(Path.AccountEdit)}>
              <ListItemWithIcon tabIndex={-1} aria-label={t('account.edit-personal-information')}>
                <Typography component='dt' className={SENTRY_CONFIG.privacy.className} variant='labelM'>
                  {t('account.email')}
                </Typography>
                <FontAwesomeIcon aria-hidden={true} icon={ICONS.farPencil} />
              </ListItemWithIcon>
              <Typography component='dd' className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
                {userDetails.data.email}
              </Typography>
              <Typography
                component='dt'
                sx={{ marginTop: unit(2) }}
                className={SENTRY_CONFIG.privacy.className}
                variant='labelM'
              >
                {t('account.phone-number')}
              </Typography>
              <Typography component='dd' className={SENTRY_CONFIG.privacy.className} variant='bodyM'>
                {userDetails.data.country_code} {userDetails.data.phone}
              </Typography>
            </ListButton>
            <Link title={t('account.booking-history')} to={to(Path.History)} disableBorderBottom={true} />
          </Stack>
        </Stack>
        {/* IPEOwnership will be conditionally displayed after APi adjustment - lack of IPE contract field */}
        {userDetails.data.has_active_contracts && <Ownership />}
        <Settings />
        <Footer>
          <ButtonSignOut variant='outlined' color='black' />
        </Footer>
        <UpdateEmailStatusMessage />
      </LayoutNavigation>
    )
  }

  return <AccountLoading to={lastPath.root} />
}

const ArrowLinkIcon = styled(LinkUnstyled)`
  height: ${unit(16)};
  display: inline-flex;
  align-items: center;
  justify-content: start;
  position: relative;
  z-index: ${Z_INDEX.navigation};
  font-size: ${unit(6)};
`

const Header = styled(Stack)`
  padding: ${unit(4.5)} ${unit(7)};
  word-break: break-word;
  border-bottom: ${unit(2.5)};
`

const Footer = styled(Box)`
  padding: ${unit(5)} ${unit(7)};
`

const UserInformation = styled(Stack)`
  padding: ${unit(3)} ${unit(7)} ${unit(3.5)} ${unit(7)};
  border-bottom: ${unit(0.25)} solid ${COLORS.vaalea[600]};
  word-break: break-word;
`

const ListButton = styled(Button)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${unit(2)} ${unit(7)} ${unit(2.5)} ${unit(7)};
  color: ${COLORS.black};
  border-bottom: ${unit(0.25)} solid ${COLORS.vaalea[600]};
  word-break: break-word;
  font-weight: normal;
` as typeof Button

const ListItemWithIcon = styled(Stack)`
  display: inline-flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  width: 100%;
  font-size: ${unit(5)};
`
